import { UserGrantsResponse } from '../../../../types/user';

/**
 * The free tier is not a product record — `createInfiniteFreeTier` writes this literal
 * id onto the grants record, and the client mirrors it in the digital products list.
 */
export const FREE_TIER_LICENSE_ID = 'free-tier';

/**
 * Session-scoped so the prompt reappears on the next sign-in but not on every reload
 * or route change within the same visit. `sessionStorage` is cleared by the browser
 * when the tab closes and explicitly on logout, which matches "once per login" closely
 * enough without needing a login timestamp on the grants record.
 */
export const UPGRADE_PROMPT_SEEN_KEY = 'UpgradePromptSeen';

/**
 * True when an active organisation membership already licenses the account.
 *
 * Joining an organisation does not clear the personal free tier: someone who signed up
 * on their own and was then added by a partner (an invite or the partner API binding the
 * existing account) keeps `licenseId: 'free-tier'` alongside the org grant. Their
 * entitlements come from the organisation, so they are not the audience for an upsell.
 * An org without a licence entitles nothing, so its members are still offered one.
 */
export const hasLicensedOrganisation = (grants: UserGrantsResponse | null | undefined): boolean =>
	(grants?.organisations ?? []).some((org) => org.status === 'active' && !!org.grants?.license);

export const isUserOnFreeTier = (grants: UserGrantsResponse | null | undefined): boolean =>
	grants?.personal?.licenseId === FREE_TIER_LICENSE_ID && !hasLicensedOrganisation(grants);

/**
 * True when the account holds no licence at all and should be enrolled on the free tier.
 *
 * Enrolment happens in the PostConfirmation trigger, so this only catches the cases the
 * trigger cannot: a transient failure during sign-up, and accounts created before
 * automatic enrolment existed. Members of an organisation are excluded — they draw their
 * entitlements from the org grant, and stacking the free tier's allowances on top would
 * silently inflate what they hold.
 */
export const needsFreeTierEnrolment = (grants: UserGrantsResponse | null | undefined): boolean =>
	!!grants && !grants.personal?.licenseId && !grants.license && (grants.organisations?.length ?? 0) === 0;

export const hasSeenUpgradePrompt = (): boolean => sessionStorage.getItem(UPGRADE_PROMPT_SEEN_KEY) === 'true';

export const markUpgradePromptSeen = (): void => {
	sessionStorage.setItem(UPGRADE_PROMPT_SEEN_KEY, 'true');
};

export const clearUpgradePromptSeen = (): void => {
	sessionStorage.removeItem(UPGRADE_PROMPT_SEEN_KEY);
};

/**
 * The prompt is an upsell, not a gate: it only ever shows to a user who is genuinely on
 * the free tier, once per session, and only after grants have actually loaded — showing
 * it against a null grants record would flash it at paid users on every cold start.
 */
export const shouldShowUpgradePrompt = (grants: UserGrantsResponse | null | undefined): boolean =>
	isUserOnFreeTier(grants) && !hasSeenUpgradePrompt();
